import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaRupeeSign, FaCreditCard } from 'react-icons/fa';
import { toast } from 'react-toastify';
import StudentService from '../../services/studentService';

const PaymentModal = ({ show, onHide, course, studentId, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'online',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens with new course
  useEffect(() => {
    if (show && course) {
      setFormData({
        amount: course.due_amount || '',
        payment_method: 'online',
        remarks: ''
      });
      setErrors({});
    }
  }, [show, course]);

  if (!course) return null;

  const dueAmount = course.due_amount || 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parseFloat(formData.amount) > dueAmount) {
      newErrors.amount = `Amount cannot exceed due amount (₹${dueAmount})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await StudentService.makePayment(studentId, {
        course_id: course.course_id,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        remarks: formData.remarks
      });
      
      toast.success(`Payment of ₹${formData.amount} successful!`);
      onSuccess(response.data);
      onHide();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCreditCard className="me-2 text-success" />
          Make Payment
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <Row>
              <Col md={8}>
                <strong>{course.course_name}</strong>
              </Col>
              <Col md={4} className="text-end">
                <span className="badge bg-primary">Due: ₹{dueAmount}</span>
              </Col>
            </Row>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Payment Amount <span className="text-danger">*</span></Form.Label>
            <div className="d-flex align-items-center">
              <div className="bg-light px-3 py-2 border rounded-start">
                <FaRupeeSign />
              </div>
              <Form.Control
                type="number"
                name="amount"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={handleChange}
                isInvalid={!!errors.amount}
                disabled={loading}
                className="rounded-start-0"
                min="1"
                max={dueAmount}
                step="1"
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.amount}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Maximum payable: ₹{dueAmount}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Payment Method</Form.Label>
            <Form.Select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="online">Online Transfer</option>
              <option value="card">Credit/Debit Card</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Remarks (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              name="remarks"
              rows={2}
              placeholder="Add any remarks"
              value={formData.remarks}
              onChange={handleChange}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Processing...
              </>
            ) : (
              'Pay Now'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentModal;